/**
 * Database — the app's own managed Postgres, browsed and written.
 *
 * Laid out after the Claude Design "Database" artboard, on the containers
 * design system: a column of everything in the database beside the app's own
 * sidebar, and the open object to its right — the kind and the name on one
 * line as the head of the screen, then a single toolbar row carrying its tabs
 * (Data · Columns · Indexes · Triggers for a table, Data · Definition for a
 * view, Definition for a function), the filter, refresh, the console, the
 * column menu and the one green button that adds whatever the tab lists.
 * A row, a new column, a new table and every statement open in a drawer
 * from the right.
 *
 * Below `xl` the column would squeeze the grid, so it becomes a sheet opened
 * by tapping the head itself; below `md` the grid becomes cards.
 *
 * It writes, but never by itself: every create, replace, drop and row change
 * shows its statement before it runs, as the app's own role, and says it is
 * not in the app's migrations.
 */
import {
  App,
  AppDatabase,
  AppDatabaseColumn,
  AppDatabaseObjects,
  AppDatabaseTable,
  AppDatabaseTableRows,
} from '@back4app2/sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import { DsButton, Eyebrow, ScreenHead, SurfaceCard } from '../components/ds';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DatabaseIcon,
  DownloadIcon,
  CheckIcon,
  FileIcon,
  MoreIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  TerminalIcon,
  TrashIcon,
} from '../components/ds/icons';
import AddColumnDialog from './database/AddColumnDialog';
import CreateTableDrawer from './database/CreateTableDrawer';
import SideDrawer from './database/SideDrawer';
import ColumnsMenu from './database/ColumnsMenu';
import { Popover, PopoverItem, SectionLabel } from './database/controls';
import { useDismiss } from './database/useDismiss';
import DatabaseExplorer, {
  explorerItems,
  functionId,
  ObjectKind,
  tableId,
} from './database/DatabaseExplorer';
import FilterInput, { ActiveFilter, FilterJoin } from './database/FilterInput';
import { ColumnsList, DefinitionView, IndexesList, TriggersList } from './database/ObjectPanels';
import RowInspector from './database/RowInspector';
import SqlDialog, { SqlDialogRequest } from './database/SqlDialog';
import UnavailableCard from './database/UnavailableCard';
import { newIndexFor, newObjectTemplate, newTriggerFor } from './database/templates';
import {
  CellValue,
  columnWidth,
  downloadCsv,
  formatApproxCount,
  formatBytes,
  isNumericType,
  qualifyName,
  quoteIdent,
  quotedTarget,
  rowsToCsv,
  sqlLiteral,
  shortTypeName,
  titleFor,
} from './database/cells';

const PAGE_SIZE = 50;

type DatabaseTab = 'data' | 'columns' | 'indexes' | 'triggers' | 'definition';

const TAB_LABEL: Record<DatabaseTab, string> = {
  data: 'Data',
  columns: 'Columns',
  indexes: 'Indexes',
  triggers: 'Triggers',
  definition: 'Definition',
};

/** For the line that stands in for a panel when nothing is open. */
const KIND_PLURAL: Record<ObjectKind, string> = {
  table: 'tables',
  view: 'views',
  function: 'functions',
};

const TABS_FOR: Record<ObjectKind, DatabaseTab[]> = {
  table: ['data', 'columns', 'indexes', 'triggers'],
  view: ['data', 'definition'],
  function: ['definition'],
};

/** The inverse of `tableId`. Split once: a table name may itself contain a dot. */
const splitTableId = (id: string): [string, string] => {
  const at = id.indexOf('.');

  return [id.slice(0, at), id.slice(at + 1)];
};

const MyAppDatabase = () => {
  const { app } = useOutletContext<{ app: App }>();
  const appId = app?.id;

  const [overview, setOverview] = useState<AppDatabase | null>(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState<string | undefined>();
  /** Bumped when a statement may have added or removed an object. */
  const [overviewToken, setOverviewToken] = useState(0);
  /**
   * The latest table list, for the one reader that is not a render: the
   * objects effect falls back to the first table when the open function turns
   * out to be gone, and it must not close over the list as it was when that
   * effect was created.
   */
  const tablesRef = useRef<AppDatabaseTable[]>([]);

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [tab, setTab] = useState<DatabaseTab>('data');
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [descending, setDescending] = useState(false);
  /** Every clause the grid is narrowed by. */
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  /** Whether all of them have to match, or any of them. */
  const [filtersJoin, setFiltersJoin] = useState<FilterJoin>('and');
  /**
   * The columns of the last page that loaded, kept so the filter bar survives
   * a page that did not. A filter the database refuses is the likeliest reason
   * for that, and the bar is where it is taken back off.
   */
  const [lastColumns, setLastColumns] = useState<AppDatabaseColumn[]>([]);
  /** Bumped to re-run the page query without changing what it asks for. */
  const [reloadToken, setReloadToken] = useState(0);
  /** Columns hidden from the grid, per table. The view only — never the query. */
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, string[]>>({});
  /** Widths dragged by hand, per table then per column. Undragged ones are
   *  still sized from their type. */
  const [columnSizes, setColumnSizes] = useState<Record<string, Record<string, number>>>({});
  /** The column being dragged, so the whole grid stops selecting text and
   *  keeps the resize cursor wherever the pointer wanders. */
  const [resizing, setResizing] = useState<string | null>(null);
  /**
   * A phone. The grid stays a grid there — it is the only shape in which one
   * value can be compared with the one under it — but it is drawn smaller:
   * type a notch down, cells tighter, and every column narrower, because 120px
   * for an `id` of one digit is a screen width spent on four columns.
   */
  /** The phone's filter, which is behind an icon until it is wanted. */
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  /**
   * Rows ticked for a bulk action, by their primary key, with the row kept so
   * the statement can be written from the values Postgres sent rather than
   * from what the cells are showing.
   */
  const [ticked, setTicked] = useState<Record<string, unknown[]>>({});
  /**
   * The row open in the drawer — its values, not its position: the page query
   * has no order of its own, so after an UPDATE the same row can come back at
   * another index. `'new'` is the drawer creating one.
   */
  const [openRow, setOpenRow] = useState<unknown[] | 'new' | null>(null);
  /** The New table form, and the name the explorer's search offered. */
  const [createTable, setCreateTable] = useState<{ name?: string } | null>(null);
  /** The table the Add column dialog is adding to. Null while closed. */
  const [addColumnTo, setAddColumnTo] = useState<{ schema: string, name: string } | null>(null);

  /** The kind the explorer is listing, which is not always the open one's. */
  const [explorerKind, setExplorerKind] = useState<ObjectKind>('table');
  const [explorerQuery, setExplorerQuery] = useState('');
  /** The explorer as a sheet, below `xl`. */
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  /**
   * The rail, on `xl` and up. Open is the right default — the list is how you
   * get to another table — but a wide grid is the reason this screen exists,
   * and 232px of it back is worth a click.
   */
  const [isRailOpen, setIsRailOpen] = useState(true);

  const [objects, setObjects] = useState<AppDatabaseObjects | null>(null);
  const [isLoadingObjects, setIsLoadingObjects] = useState(false);
  const [objectsError, setObjectsError] = useState<string | undefined>();
  /** Bumped after a statement runs, so the list it changed catches up. */
  const [objectsToken, setObjectsToken] = useState(0);
  /** The SQL dialog's current question. Null while it is closed. */
  const [dialog, setDialog] = useState<SqlDialogRequest | null>(null);

  const [rows, setRows] = useState<AppDatabaseTableRows | null>(null);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [rowsError, setRowsError] = useState<string | undefined>();

  useEffect(() => {
    if (!appId) {
      return;
    }

    let cancelled = false;

    setIsLoadingOverview(true);
    setOverviewError(undefined);

    back4app2
      .appDatabase(appId)
      .then(result => {
        if (cancelled) {
          return;
        }
        setOverview(result);
        tablesRef.current = result.tables;
        // The first table is opened for you — but only when nothing is open:
        // this re-runs after every statement, and reseating the selection
        // there threw the reader back to the first table every time they
        // created an index on another one.
        setSelectedId(current => {
          // A function's id is not in this list — it comes from the objects
          // query — so it is kept on its prefix alone.
          if (current && current.startsWith('fn:')) {
            return current;
          }

          if (current && result.tables.some(table => tableId(table) === current)) {
            return current;
          }

          return result.tables.length > 0 ? tableId(result.tables[0]) : undefined;
        });
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setOverviewError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingOverview(false);
        }
      });

    return () => { cancelled = true; };
  }, [appId, overviewToken]);

  // Always, not only on Indexes and Triggers: the explorer lists the
  // database's functions whatever is open, and one query brings them back
  // together with the open table's indexes and triggers.
  useEffect(() => {
    if (!appId) {
      return;
    }

    let cancelled = false;

    setIsLoadingObjects(true);
    setObjectsError(undefined);

    // A function's id is not a table, and asking for `fn:public.x()`'s indexes
    // would be asking about a relation that does not exist.
    const [schema, name] = selectedId && !selectedId.startsWith('fn:')
      ? splitTableId(selectedId)
      : [];

    back4app2
      .appDatabaseObjects(appId, schema, name)
      .then(result => {
        if (cancelled) {
          return;
        }

        setObjects(result);

        // The open function is gone — dropped, here or elsewhere. It falls
        // back to the first table rather than to nothing.
        setSelectedId(current => {
          if (!current || !current.startsWith('fn:')) {
            return current;
          }

          if (result.functions.some(fn => functionId(fn) === current)) {
            return current;
          }

          const first = tablesRef.current[0];

          return first ? tableId(first) : undefined;
        });
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setObjectsError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingObjects(false);
        }
      });

    return () => { cancelled = true; };
  }, [appId, selectedId, objectsToken]);

  const selectedFunction = useMemo(
    () => (objects?.functions || []).find(fn => functionId(fn) === selectedId),
    [objects, selectedId]
  );

  const selectedTable: AppDatabaseTable | undefined = useMemo(
    () => overview?.tables.find(table => tableId(table) === selectedId),
    [overview, selectedId]
  );

  // The page query also brings the column list, which is what Columns draws —
  // so it runs whichever tab is open.
  useEffect(() => {
    if (!appId || !selectedTable) {
      setRows(null);
      setLastColumns([]);
      return;
    }

    let cancelled = false;

    setIsLoadingRows(true);
    setRowsError(undefined);

    back4app2
      .appDatabaseTableRows(appId, selectedTable.schema, selectedTable.name, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        orderBy,
        descending,
        filters,
        filtersJoin,
      })
      .then(result => {
        if (!cancelled) {
          setRows(result);
          setLastColumns(result.columns);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setRows(null);
          setRowsError(e.message);

          // The table went away under us — dropped by a migration, by a
          // teammate, or by the console on this very screen. Sitting on an
          // error about a name that no longer exists is a dead end: re-read
          // the list and let the screen land on something that is there.
          if (/does not exist/i.test(e.message)) {
            setSelectedId(undefined);
            setOverviewToken(token => token + 1);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingRows(false);
        }
      });

    return () => { cancelled = true; };
  }, [appId, selectedTable, page, orderBy, descending, filters, filtersJoin, reloadToken]);

  // A tick belongs to the page it was made on: the rows under it are different
  // after a sort, a filter or a step, and a delete built from stale keys would
  // be a delete nobody chose.
  useEffect(() => {
    setTicked({});
  }, [selectedId, page, orderBy, descending, filters, filtersJoin]);

  // What the screen is about: the open object's kind, or — with nothing open —
  // the kind the list is showing. Defaulting to 'table' there had the head say
  // "table" while the rail sat on Functions.
  const kind: ObjectKind = selectedFunction
    ? 'function'
    : selectedTable
      ? (selectedTable.kind !== 'table' ? 'view' : 'table')
      : explorerKind;

  // The explorer follows what opens, so the list shows the open object.
  useEffect(() => {
    if (selectedFunction || selectedTable) {
      setExplorerKind(kind);
    }
  }, [selectedId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsCompact(query.matches);

    query.addEventListener('change', onChange);

    return () => query.removeEventListener('change', onChange);
  }, []);

  const closeDrawer = useCallback(() => setOpenRow(null), []);
  const closeSheet = useCallback(() => setIsSheetOpen(false), []);
  const openDrawer = setOpenRow;

  const hidden = useMemo(
    () => (selectedId && hiddenColumns[selectedId]) || [],
    [hiddenColumns, selectedId]
  );

  /** Indexes into `rows.columns` of what the grid draws, in order. */
  const visibleColumns = useMemo(
    () => (rows?.columns || [])
      .map((column, index) => ({ column, index }))
      .filter(({ column }) => !hidden.includes(column.name)),
    [rows, hidden]
  );

  /** What the filter bar names: this page's columns, or the last page's. */
  const filterColumns = rows?.columns.length ? rows.columns : lastColumns;

  /** Widths the reader dragged, per table then per column. */
  const dragged = useMemo(
    () => (selectedId && columnSizes[selectedId]) || {},
    [columnSizes, selectedId]
  );

  const columnWidths = useMemo(
    () => visibleColumns.map(({ column }) => dragged[column.name]
      || (isCompact
        ? Math.max(68, Math.round(columnWidth(column) * 0.72))
        : columnWidth(column))),
    [visibleColumns, dragged, isCompact]
  );

  /**
   * Dragging the line between two headers.
   *
   * The pointer is captured on the handle, so the drag keeps following it over
   * the grid, out of the table and past the window edge — a resize that stops
   * when the cursor leaves the 9px it started in is a resize nobody can aim.
   * A double-click gives the column back to the type that sized it.
   */
  const startResize = useCallback((name: string, from: number, event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;

    setResizing(name);

    // On the window, not on the handle: the handle is 12px wide and the
    // pointer leaves it on the first pixel of the drag. Pointer capture would
    // do the same job, but it is tied to an element React re-renders on every
    // move — the window is not.
    const onMove = (move: PointerEvent) => {
      const next = Math.max(72, Math.round(from + (move.clientX - startX)));

      setColumnSizes(current => (selectedId
        ? { ...current, [selectedId]: { ...current[selectedId], [name]: next } }
        : current));
    };
    const onUp = () => {
      setResizing(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [selectedId]);

  const resetWidth = useCallback((name: string) => {
    setColumnSizes(current => {
      if (!selectedId || !current[selectedId]) {
        return current;
      }

      const next = { ...current[selectedId] };

      delete next[name];

      return { ...current, [selectedId]: next };
    });
  }, [selectedId]);

  const items = useMemo(
    () => explorerItems(overview?.tables || [], objects?.functions || []),
    [overview, objects]
  );

  const openObject = useCallback((id: string) => {
    if (id === selectedId) {
      return;
    }
    setSelectedId(id);
    // A new object is a new grid: its columns are different, so a sort or a
    // filter naming a column of the previous one is not state to keep.
    setPage(0);
    setOrderBy(undefined);
    setDescending(false);
    setFilters([]);
    setOpenRow(null);
  }, [selectedId]);

  const toggleSort = useCallback((column: string) => {
    setPage(0);
    if (orderBy === column) {
      // Third click clears it rather than cycling back to ASC — unsorted is a
      // real state here (it is the table's own storage order).
      if (descending) {
        setOrderBy(undefined);
        setDescending(false);
      } else {
        setDescending(true);
      }
      return;
    }
    setOrderBy(column);
    setDescending(false);
  }, [orderBy, descending]);

  const changeFilter = useCallback((next: ActiveFilter[]) => {
    setFilters(next);
    setPage(0);
  }, []);

  const exportPage = useCallback(() => {
    if (!rows || !selectedTable) {
      return;
    }
    downloadCsv(
      `${tableId(selectedTable)}-${page * PAGE_SIZE + 1}-${page * PAGE_SIZE + rows.rows.length}.csv`,
      rowsToCsv(rows.columns.map(column => column.name), rows.rows)
    );
  }, [rows, selectedTable, page]);

  // Only the FIRST load takes the screen. Every statement reloads this list,
  // and swapping the screen for a spinner unmounted the dialog that ran it.
  if (isLoadingOverview && !overview) {
    return <>
      <ScreenHead title="Database" />
      <div className="flex justify-center py-16"><LoadingSpinner /></div>
    </>;
  }

  if (overviewError) {
    return <>
      <ScreenHead title="Database" />
      <SurfaceCard tone="surface" className="p-6">
        <p className="text-ds-135 text-navy-100">We couldn't read this app's database.</p>
        <p className="mt-1 text-ds-125 text-navy-300">{overviewError}</p>
      </SurfaceCard>
    </>;
  }

  if (!overview || !overview.available) {
    // The two states that answer with a full hero carry their own headline —
    // "Database / Browsing is off while the database is blocked" above "This
    // database is blocked" was the same sentence twice, in two sizes. The two
    // momentary ones are a short card and still need naming.
    const hasHero = !overview?.reason
      || overview.reason === 'NO_DATABASE'
      || overview.reason === 'CANCELED';

    return <>
      {hasHero ? null : <ScreenHead title="Database" />}
      <UnavailableCard
        appId={appId}
        plan={app?.mainService?.mainServiceEnvironment?.plan}
        reason={overview?.reason}
        purgeScheduledAt={overview?.purgeScheduledAt}
      />
    </>;
  }

  const openConsole = () => setDialog({
    title: 'SQL console',
    blurb: 'One statement, run as your app\'s own database role.',
    sql: '',
    action: 'Run',
  });

  // The three queries the button re-runs are the three the screen is made of,
  // so it spins while any of them is in flight.
  const isReloading = isLoadingOverview || isLoadingObjects || isLoadingRows;

  const reloadAll = () => {
    setOverviewToken(token => token + 1);
    setObjectsToken(token => token + 1);
    setReloadToken(token => token + 1);
  };

  const selectExplorerKind = (next: ObjectKind) => {
    setExplorerKind(next);

    // Switching kind opens its first object, so the page always shows what the
    // list is about. When the kind holds nothing, the selection is CLEARED
    // rather than left alone: keeping it meant picking Functions on a database
    // with no functions left a table open beside a list that said otherwise.
    const first = items[next][0];

    if (first) {
      openObject(first.id);
      return;
    }

    setSelectedId(undefined);
    setPage(0);
    setOrderBy(undefined);
    setDescending(false);
    setFilters([]);
    setOpenRow(null);
  };

  const hasObject = !!(selectedFunction || selectedTable);
  const objectName = selectedFunction?.name
    || (selectedTable ? qualifyName(selectedTable.schema, selectedTable.name) : `No ${kind} open`);
  const objectKindLabel = kind;

  const tabs = TABS_FOR[kind];
  const activeTab: DatabaseTab = tabs.includes(tab) ? tab : tabs[0];
  const isTable = selectedTable?.kind === 'table';
  const target = selectedTable ? qualifyName(selectedTable.schema, selectedTable.name) : '';

  const definitionSql = selectedFunction
    ? selectedFunction.definition
    : selectedTable?.definition
      ? `CREATE ${selectedTable.kind === 'materialized view' ? 'MATERIALIZED VIEW' : 'VIEW'} ${target} AS\n${selectedTable.definition}`
      : '';

  // A grid row is the open one when its primary key matches — or, on a table
  // without one, when it is the very same array.
  const keyIndexes = (rows?.columns || [])
    .map((column, index) => (column.isPrimaryKey ? index : -1))
    .filter(index => index >= 0);
  const isOpenRow = (row: unknown[]) => Array.isArray(openRow) && (
    openRow === row || (keyIndexes.length > 0
      && keyIndexes.every(index => String(openRow[index]) === String(row[index])))
  );

  /**
   * Ticking rows needs a name for each one that survives a reload, and a
   * primary key is the only thing a table has that does. A view, or a table
   * without one, gets no checkboxes — the same rule the row drawer already
   * uses to decide whether it may write.
   */
  const canTick = isTable && keyIndexes.length > 0;
  const rowKey = (row: unknown[]) =>
    keyIndexes.map(index => String(row[index])).join('\u0000');
  const tickedKeys = Object.keys(ticked);
  const pageRows = rows?.rows || [];
  const tickedOnPage = pageRows.filter(row => ticked[rowKey(row)]).length;

  const toggleRow = (row: unknown[]) => setTicked(current => {
    const key = rowKey(row);
    const next = { ...current };

    if (next[key]) {
      delete next[key];
    } else {
      next[key] = row;
    }

    return next;
  });

  const togglePage = () => setTicked(current => {
    const next = { ...current };

    if (tickedOnPage === pageRows.length) {
      pageRows.forEach(row => delete next[rowKey(row)]);
    } else {
      pageRows.forEach(row => { next[rowKey(row)] = row; });
    }

    return next;
  });

  /**
   * `DELETE … WHERE (key) IN (…)` — one statement, shown before it runs like
   * every other write on this screen. Composite keys become a row constructor,
   * which is the form Postgres takes for "any of these rows".
   */
  const deleteTicked = () => {
    if (!selectedTable || !canTick || tickedKeys.length === 0) {
      return;
    }

    const keyColumns = keyIndexes.map(index => (rows?.columns || [])[index]);
    const target = quotedTarget(selectedTable.schema, selectedTable.name);
    const names = keyColumns.map(column => quoteIdent(column.name));
    const tuples = tickedKeys.map(key => {
      const row = ticked[key];

      return keyIndexes.length === 1
        ? sqlLiteral(row[keyIndexes[0]])
        : `(${keyIndexes.map(index => sqlLiteral(row[index])).join(', ')})`;
    });

    setDialog({
      title: `Delete ${tickedKeys.length} row${tickedKeys.length === 1 ? '' : 's'}`,
      blurb: `From ${selectedTable.name}. Rows referenced by another table make the delete fail rather than cascade — which is the foreign key doing its job.`,
      sql: [
        `DELETE FROM ${target}`,
        ` WHERE ${names.length === 1 ? names[0] : `(${names.join(', ')})`} IN (`,
        `  ${tuples.join(',\n  ')}`,
        ');',
      ].join('\n'),
      action: 'Delete',
    });
  };

  // The one green button of the bar: whatever this tab lists, one more of it.
  const primary: { label: string, icon: React.ReactNode, onClick: () => void } | null = !hasObject
    // Nothing open — an empty database, or a kind that holds none. The button
    // makes the first one, which is the only thing left to do here.
    ? {
      label: `New ${kind}`,
      icon: <PlusIcon size={16} />,
      onClick: () => (kind === 'table'
        ? setCreateTable({})
        : setDialog(newObjectTemplate(kind))),
    }
    : activeTab === 'data'
      ? (isTable ? { label: 'Add row', icon: <PlusIcon size={16} />, onClick: () => openDrawer('new') } : null)
      : activeTab === 'columns'
        ? (isTable && selectedTable
          ? {
            label: 'Add column',
            icon: <PlusIcon size={16} />,
            onClick: () => setAddColumnTo({ schema: selectedTable.schema, name: selectedTable.name }),
          }
          : null)
        : activeTab === 'indexes' && selectedTable
          ? { label: 'Add index', icon: <PlusIcon size={16} />, onClick: () => setDialog(newIndexFor(target, selectedTable.name)) }
          : activeTab === 'triggers' && selectedTable
            ? { label: 'Add trigger', icon: <PlusIcon size={16} />, onClick: () => setDialog(newTriggerFor(target, selectedTable.name)) }
            : activeTab === 'definition'
              // CREATE OR REPLACE is how a view or a function is changed. A
              // materialized view has no such form, so it gets no button that
              // would fail.
              ? (selectedFunction
                ? {
                  label: 'Edit definition',
                  icon: <FileIcon size={16} />,
                  onClick: () => setDialog({
                    title: `Replace function ${selectedFunction.name}`,
                    blurb: `CREATE OR REPLACE overwrites the current body of ${selectedFunction.name}. Callers keep working if the signature stays the same.`,
                    sql: selectedFunction.definition,
                    action: 'Replace',
                  }),
                }
                : selectedTable && selectedTable.kind === 'view' && selectedTable.definition
                  ? {
                    label: 'Edit definition',
                    icon: <FileIcon size={16} />,
                    onClick: () => setDialog({
                      title: `Replace view ${selectedTable.name}`,
                      blurb: `CREATE OR REPLACE overwrites the current definition of ${selectedTable.name}. Columns cannot be renamed or dropped this way.`,
                      sql: `CREATE OR REPLACE VIEW ${target} AS\n${selectedTable.definition}`,
                      action: 'Replace',
                    }),
                  }
                  : null)
              : null;

  // Dropping a function belongs beside replacing it: both act on the definition
  // on screen, and alone under the code the button was a red thing at the end of
  // a scroll, far from the only other control the tab has.
  const dropFunction = activeTab === 'definition' && selectedFunction
    ? () => setDialog({
      title: `Drop ${selectedFunction.kind}`,
      blurb: `Removes ${selectedFunction.name}. A trigger that calls it makes the drop fail.`,
      // The arguments are part of a function's identity — two can share a
      // name — so the drop names them.
      sql: `DROP ${selectedFunction.kind === 'procedure' ? 'PROCEDURE' : 'FUNCTION'} IF EXISTS ${qualifyName(selectedFunction.schema, selectedFunction.name)}(${selectedFunction.arguments});`,
      action: 'Drop',
    })
    : null;

  // No count beside the tabs. Each of these panels heads itself and lists the
  // very things it was counting — "4 columns on customers." sat above four
  // columns on customers.

  // The footer of the grid is where the object's own numbers live now: what
  // this page is and how big the table is on disk. In the head they sat above a
  // grid that then repeated the count in its pager.
  //
  // The engine version used to end this line and is gone: which server runs the
  // database is ours to choose and ours to upgrade, and printing it under every
  // table made it read like something the customer picked.
  const rowsLabel = [
    rows && rows.rows.length > 0
      ? `Rows ${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + rows.rows.length}${
        selectedTable?.kind === 'table' && !filters.length ? ` of ~${formatApproxCount(selectedTable.estimatedRowCount)}` : ''
      }`
      : '—',
    selectedTable ? formatBytes(selectedTable.sizeBytes) : undefined,
  ].filter(Boolean).join(' · ');

  /**
   * The Data tab with a grid in it, which is the one thing on this screen that
   * should take whatever height the window has: a table capped at 560px left
   * the bottom third of a laptop empty and paged every 18 rows.
   */
  const isGridTab = hasObject
    && activeTab === 'data'
    && !rowsError
    && !!rows
    && rows.columns.length > 0;

  /**
   * Whether the phone's one toolbar line is showing the filter rather than the
   * tabs. An applied filter always shows: chips hidden behind an icon are a
   * page quietly narrowed by something nobody can see.
   */
  const showsFilterBar = isFilterOpen || filters.length > 0;

  const explorer = (isTouch: boolean) => (
    <DatabaseExplorer
      items={items}
      kind={explorerKind}
      query={explorerQuery}
      selectedId={selectedId}
      isTouch={isTouch}
      onQuery={setExplorerQuery}
      onKind={selectExplorerKind}
      onSelect={id => { openObject(id); if (isTouch) { closeSheet(); } }}
      onNew={(next, name) => {
        if (isTouch) { closeSheet(); }
        // A table is created with its columns, in a form; views and functions
        // are written as SQL.
        if (next === 'table') {
          setCreateTable({ name });
        } else {
          setDialog(newObjectTemplate(next, name));
        }
      }}
    />
  );

  return <>
    {/* On `xl` the screen takes the whole region beside the app's sidebar —
        edge to edge, full height — so the explorer can run top to bottom
        and the object scroll on its own beside it. */}
    {/* `min-h-0` only where there is a height to divide up. Below `xl` this
        row is a plain block in a scrolling page: keeping it there squeezed the
        row to the viewport and let the grid spill out the bottom, under the
        mobile tab bar, past the padding the page keeps for it. */}
    <div className="relative flex xl:min-h-0 xl:-mx-8 xl:-mt-8 xl:-mb-24 xl:h-[calc(100%+8rem)]">
      {isRailOpen ? (
        <aside className="relative hidden xl:flex flex-col w-[232px] flex-none h-full border-r border-navy-500">
          {explorer(false)}
          <RailHandle isOpen onClick={() => setIsRailOpen(false)} />
        </aside>
      ) : (
        <RailHandle isOpen={false} onClick={() => setIsRailOpen(true)} />
      )}

      <div className={`flex-1 min-w-0 b4-thin xl:h-full xl:px-8 xl:pt-7 ${
        // The grid keeps its own scrollbar, so the page must not have a second
        // one — two nested scrollers were what capped the table at 560px.
        //
        // It runs to the bottom edge of the window. The support launcher is
        // fixed in that corner, so the pager reserves its width instead of the
        // whole screen reserving its height — see the pager's own padding.
        isGridTab
          ? 'xl:pb-4 xl:overflow-y-auto xl:flex xl:flex-col'
          : 'xl:pb-24 xl:overflow-y-auto'
      }`}>
        {/* No reading measure here: this screen is a grid, and a grid is more
            useful the more columns fit on it. The 1280px cap that keeps prose
            readable was leaving empty page beside a table scrolling sideways. */}
        <div className={`flex flex-col gap-4 xl:gap-5 ${isGridTab ? 'xl:flex-1 xl:min-h-0' : ''}`}>

          {/* With nothing open there are no tabs and no filter, so the head
              and the three controls share one row instead of leaving a band
              of empty page between them. */}
          <div className={hasObject
            ? 'flex flex-wrap items-center gap-1.5 xl:flex-col xl:items-stretch xl:gap-5'
            : 'flex flex-wrap items-center justify-between gap-3'}>
          {/* ── The object IS the head of this screen. ──
              One line, the kind in front of the name: "Database" over it named
              the section the sidebar already names, and the meta under it said
              the size twice — the grid's own footer carries it now.

              Below `xl` the whole head is the way to another object: the rail
              is a sheet there, and a head that does nothing beside a separate
              "Change" button was two controls for one job. */}
          <div
            {...(hasObject ? {
              role: 'button',
              tabIndex: 0,
              onClick: () => { setExplorerQuery(''); setIsSheetOpen(true); },
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setExplorerQuery('');
                  setIsSheetOpen(true);
                }
              },
            } : {})}
            className={`flex min-w-0 ${
              hasObject
                // A phone gets the picker the design draws: a bordered box
                // with the kind over the name, sized like the controls beside
                // it. On `xl` the rail IS the list, so the head goes back to
                // being the screen's title.
                ? 'b4-focus items-center gap-2 h-10 px-2.5 max-w-[46vw] rounded-[6px] border border-navy-400 bg-navy-700 cursor-pointer'
                  + ' xl:items-baseline xl:gap-2.5 xl:h-auto xl:max-w-none xl:px-0 xl:rounded-none xl:border-0 xl:bg-transparent xl:pointer-events-none xl:cursor-default'
                : 'items-baseline gap-2.5'
            }`}
          >
            <span className={hasObject ? 'flex flex-col min-w-0 xl:flex-row xl:items-baseline xl:gap-2.5' : 'contents'}>
              <Eyebrow>{objectKindLabel}</Eyebrow>
              <span className="min-w-0 truncate font-courrier text-ds-115 font-semibold text-white xl:font-sora xl:text-ds-20 xl:tracking-[-0.02em]">
                {objectName}
              </span>
            </span>
            {hasObject ? (
              <span className="xl:hidden flex items-center flex-none text-navy-200">
                <ChevronDownIcon size={14} />
              </span>
            ) : null}
          </div>

          {/* ── One row: what to show, what to look for, and the one green
               button. Refresh and the console moved in here as icons — they
               belong to the table on screen, and as labelled buttons up in the
               head they outweighed it.

               It renders with nothing open too: an empty database still needs
               the way to make its first table, and the console that could. ── */}
          {/* `gap-1.5` below `xl`: at `gap-2` the section name and six 40px
              controls come to more than a 390px screen, and that is the whole
              difference between one row and two. */}
          <div className="flex flex-wrap xl:flex-nowrap items-center gap-1 xl:gap-2 min-w-0">
              {/* Machine-set and upper-case, like every other label naming a
                  part of the database on this screen, and the open one is
                  filled rather than underlined: an underline on a row that
                  also holds a field with its own bottom edge read as two
                  things underlining each other. */}
              {/* Flush with the column, not pulled left by the tab's padding:
                  the open tab is a filled pill, and a pill hanging into the
                  page gutter reads as broken far louder than a label sitting
                  a few pixels in. The padding is kept small so the two edges
                  stay close. */}
              {/* Below `xl` the four sections are one control. Four pills,
                  a search, a menu and a green button do not fit 390px — they
                  fit by scrolling, which is how "TRIGGE" ended up next to a
                  button. Named and picked, they take the width of one word. */}
              {hasObject ? (
                <TabsMenu
                  tabs={tabs}
                  active={activeTab}
                  onPick={value => { setTab(value); setOpenRow(null); }}
                  className={showsFilterBar ? 'hidden' : 'xl:hidden'}
                />
              ) : null}

              {hasObject ? (
              <div className={`items-center gap-1 flex-1 min-w-0 xl:w-auto xl:flex-none max-w-full overflow-x-auto b4-thin [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                // A phone has one line for all of this. The filter, when it is
                // open, is what that line is for — the tabs come back the
                // moment it closes.
                showsFilterBar ? 'hidden xl:flex' : 'hidden xl:flex'
              }`}>
                {tabs.map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => { setTab(value); setOpenRow(null); }}
                    aria-current={value === activeTab ? 'page' : undefined}
                    className={[
                      'b4-focus flex-none px-2 md:px-3 min-h-[44px] md:min-h-[34px] rounded-[8px] whitespace-nowrap',
                      // A notch down and a notch tighter on a phone: the four
                      // of them were six pixels wider than the line, which is
                      // all it takes to put a scrollbar under a row that has
                      // nothing to scroll to.
                      'font-courrier text-ds-115 md:text-ds-125 uppercase tracking-[0.06em] md:tracking-[0.08em] transition-colors duration-[120ms]',
                      value === activeTab
                        ? 'bg-navy-700 text-white font-semibold'
                        : 'text-navy-300 hover:text-white',
                    ].join(' ')}
                  >
                    {TAB_LABEL[value]}
                  </button>
                ))}
              </div>
              ) : null}

              {/* ── A phone: one line for all of it. ──
                   The filter is behind its own icon and opens into the row,
                   and everything else is one menu. Four 44px buttons plus a
                   field wanted two lines and 100px of a 780px screen, to hold
                   controls that are used one at a time. */}
              {hasObject && activeTab === 'data' && filterColumns.length > 0 ? (
                <div className="flex xl:hidden items-center gap-1 min-w-0 flex-none">
                  {/* Two icons together on the left and the green button on
                      the right: the filter opens between them when it is
                      wanted, so nothing jumps and the row is never one icon
                      marooned beside an empty stretch. */}
                  <IconButton
                    onClick={() => setIsFilterOpen(open => !open)}
                    label={isFilterOpen ? 'Hide the filter' : 'Filter rows'}
                    hasTooltip={!isCompact}
                  >
                    <SearchIcon size={16} />
                  </IconButton>
                  <IconButton onClick={reloadAll} isBusy={isReloading} label="Refresh" hasTooltip={!isCompact}>
                    <RefreshIcon size={16} className={isReloading ? 'animate-spin' : undefined} />
                  </IconButton>

                  <MoreMenu
                    onExport={exportPage}
                    canExport={!!rows && rows.rows.length > 0}
                    onConsole={openConsole}
                    columns={rows?.columns || []}
                    hidden={hidden}
                    onHiddenChange={next => selectedId && setHiddenColumns(current => ({ ...current, [selectedId]: next }))}
                  />

                  {showsFilterBar ? (
                    <div className="relative flex-1 min-w-[12ch] order-first">
                      <FilterInput
                        columns={filterColumns}
                        filters={filters}
                        join={filtersJoin}
                        isCompact
                        onChange={changeFilter}
                        onJoin={next => { setFiltersJoin(next); setPage(0); }}
                      />
                    </div>
                  ) : null}

                  {/* The one green button ends this row, as it ends the
                      desktop's. Beside the tabs it was eating the width they
                      scroll in — "Triggers" was cut off before anyone had
                      scrolled anywhere. */}
                  {primary ? (
                    <DsButton
                      variant="primary"
                      size="md"
                      onClick={primary.onClick}
                      ariaLabel={primary.label}
                      // A square of the same 44px as the icons beside it while
                      // it is only a plus; from `sm` it takes its words back
                      // and sizes itself again.
                      className="!w-10 !h-10 !px-0 xl:!w-[30px] xl:!h-[30px]"
                    >
                      {/* The plus alone: the one green button of the screen,
                          named for a screen reader and in its tooltip. */}
                      {primary.icon}
                    </DsButton>
                  ) : null}
                </div>
              ) : null}

              {hasObject && activeTab === 'data' && filterColumns.length > 0 ? (
                <div className="hidden xl:flex flex-wrap items-center gap-2 min-w-0 order-3 w-full xl:flex-nowrap xl:order-none xl:w-auto xl:flex-1">
                  {/* The field takes everything the row does not need, so the
                      three icons land against the green button on the right.
                      Capped, they left a gap in the middle of the bar and the
                      icons floated in it. */}
                  <div className="relative w-full xl:w-auto xl:flex-1 min-w-0">
                    <FilterInput
                      columns={filterColumns}
                      filters={filters}
                      join={filtersJoin}
                      onChange={changeFilter}
                      onJoin={next => { setFiltersJoin(next); setPage(0); }}
                    />
                  </div>
                  <IconButton onClick={reloadAll} isBusy={isReloading} label="Refresh">
                    <RefreshIcon size={16} className={isReloading ? 'animate-spin' : undefined} />
                  </IconButton>
                  {/* Beside refresh, not down in the pager: both act on the
                      page that is on screen, and as a labelled button at the
                      bottom it outweighed the paging it sat next to. */}
                  <IconButton
                    onClick={exportPage}
                    isDisabled={!rows || rows.rows.length === 0}
                    label="Export this page as CSV"
                  >
                    <DownloadIcon size={16} />
                  </IconButton>
                  <IconButton onClick={openConsole} label="SQL console">
                    <TerminalIcon size={16} />
                  </IconButton>
                  {rows && rows.columns.length > 0 ? (
                  <ColumnsMenu
                    columns={rows.columns}
                    hidden={hidden}
                    onChange={next => selectedId && setHiddenColumns(current => ({ ...current, [selectedId]: next }))}
                  />
                  ) : null}
                </div>
              ) : (
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="flex-1" />
                  <IconButton onClick={reloadAll} isBusy={isReloading} label="Refresh">
                    <RefreshIcon size={16} className={isReloading ? 'animate-spin' : undefined} />
                  </IconButton>
                  <IconButton onClick={openConsole} label="SQL console">
                    <TerminalIcon size={16} />
                  </IconButton>
                </div>
              )}

              {primary ? (
                <div className={`flex-col flex-none w-auto order-4 xl:order-none xl:flex ${
                  // The phone's own toolbar row carries it; two would be two
                  // green buttons on one screen.
                  hasObject && activeTab === 'data' && filterColumns.length > 0 ? 'hidden' : 'flex'
                }`}>
                  <DsButton variant="primary" size="md" onClick={primary.onClick}>
                    {primary.icon} {primary.label}
                  </DsButton>
                </div>
              ) : null}

              {/* Last, after the green one: the row reads left to right from the
                  everyday action to the destructive one, and the drop is not
                  what anyone came to this tab to do. */}
              {dropFunction ? (
                <div className="flex flex-col w-full md:w-auto md:flex-none">
                  <DsButton variant="danger" size="md" onClick={dropFunction}>
                    <TrashIcon size={16} /> Drop function
                  </DsButton>
                </div>
              ) : null}
          </div>

          </div>

          {/* ── The tab. ── */}
          {!hasObject ? (
            overview.tables.length === 0 ? <EmptyDatabase /> : (
              <p className="m-0 text-ds-135 text-navy-300">
                {items[kind].length === 0
                  ? `This database has no ${KIND_PLURAL[kind]}.`
                  : `Pick one of the ${KIND_PLURAL[kind]} on the left.`}
              </p>
            )
          ) : activeTab === 'data' ? (
            isLoadingRows && !rows ? (
              <div className="flex justify-center py-20"><LoadingSpinner /></div>
            ) : rowsError ? (
              <SurfaceCard tone="surface" className="p-6">
                <p className="text-ds-135 text-navy-100">We couldn't read this table.</p>
                <p className="mt-1 text-ds-125 text-navy-300">{rowsError}</p>
              </SurfaceCard>
            ) : rows && rows.columns.length > 0 ? <>
              {/* Grid from `md` up. */}
              {/* One table, every width. A phone scrolls it sideways — which
                  is what a table is for — rather than being handed a
                  different shape of the same rows, where a value could not be
                  compared with the one under it. */}
              <SurfaceCard tone="surface" className="block overflow-hidden xl:flex xl:flex-col xl:flex-1 xl:min-h-0">
                <div className={`overflow-auto b4-thin max-h-[560px] xl:max-h-none xl:flex-1 xl:min-h-[260px] ${
                  isLoadingRows ? 'opacity-50' : ''
                } ${resizing ? 'select-none cursor-col-resize' : ''}`}>
                  <table
                    className="min-w-full table-fixed border-collapse"
                    style={{ width: columnWidths.reduce((sum, width) => sum + width, canTick ? 40 : 0) }}
                  >
                    <colgroup>
                      {canTick ? <col style={{ width: 40 }} /> : null}
                      {columnWidths.map((width, index) => (
                        <col key={index} style={{ width }} />
                      ))}
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        {canTick ? (
                          <th className="sticky top-0 z-[1] bg-navy-700 border-b border-navy-500 border-r border-navy-600 px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={pageRows.length > 0 && tickedOnPage === pageRows.length}
                              ref={node => {
                                if (node) {
                                  node.indeterminate = tickedOnPage > 0 && tickedOnPage < pageRows.length;
                                }
                              }}
                              onChange={togglePage}
                              aria-label="Select every row on this page"
                              className="b4-focus w-4 h-4 align-middle cursor-pointer accent-action"
                            />
                          </th>
                        ) : null}
                        {visibleColumns.map(({ column }, position) => {
                          const isSorted = orderBy === column.name;
                          const isNumeric = isNumericType(column.dataType);

                          return (
                            <th
                              key={column.name}
                              // A rule down each column's right edge, so a
                              // wide row is read as cells rather than as one
                              // line of words. Darker under the header, which
                              // already has its own tint.
                              className="sticky top-0 z-[1] bg-navy-700 border-b border-navy-500 border-r border-navy-600 last:border-r-0 px-2.5 py-2 md:px-3.5 md:py-2.5 text-left font-normal [position:sticky]"
                            >
                              {/* The type belongs in the header: it decides what
                                  a filter on this column may say and how the
                                  value below should be read, and it was only on
                                  the Columns tab. */}
                              <button
                                type="button"
                                onClick={() => toggleSort(column.name)}
                                title={`${column.name} · ${shortTypeName(column.dataType)}${column.isPrimaryKey ? ' · primary key' : ''}${column.isNullable ? '' : ' · not null'} — click to sort`}
                                className={`b4-focus flex items-baseline gap-1.5 w-full ${isNumeric ? 'justify-end' : ''}`}
                              >
                                <span className="flex flex-col gap-0.5 min-w-0">
                                  <span className="truncate font-courrier text-ds-115 md:text-ds-125 text-white">{column.name}</span>
                                  <span className="truncate font-courrier text-ds-105 tracking-[0.06em] text-navy-300">
                                    {shortTypeName(column.dataType)}
                                  </span>
                                </span>
                                {isSorted ? (
                                  <span className="flex-none text-action-soft text-[length:calc(12px*var(--b4-type,1))]">{descending ? '↓' : '↑'}</span>
                                ) : null}
                              </button>

                              {/* The line between two headers, with a hand's
                                  width of it to grab: the rule itself is 1px,
                                  and 1px is not a target. The grabbable strip
                                  straddles the border so the cursor changes
                                  wherever it looks like it should. */}
                              {/* The same line, grabbed from the other side.
                                  A handle can only live inside its own cell —
                                  a sticky header is its own stacking context,
                                  so anything hanging over the neighbour is
                                  painted and clicked by the neighbour — so
                                  each border gets two: this column's left
                                  edge resizes the column before it, and the
                                  band around the line ends up symmetrical. */}
                              {position > 0 ? (
                                <span
                                  role="separator"
                                  aria-orientation="vertical"
                                  aria-label={`Resize ${visibleColumns[position - 1].column.name}`}
                                  title="Drag to resize · double-click to reset"
                                  onPointerDown={event => startResize(
                                    visibleColumns[position - 1].column.name,
                                    columnWidths[position - 1],
                                    event
                                  )}
                                  onDoubleClick={() => resetWidth(visibleColumns[position - 1].column.name)}
                                  onClick={event => event.stopPropagation()}
                                  className="absolute top-0 left-0 z-[2] w-2 h-full cursor-col-resize select-none touch-none"
                                />
                              ) : null}

                              {/* Wholly inside its own cell, never straddling
                                  the border: a sticky header is its own
                                  stacking context, so anything hanging over
                                  the next column was painted — and clicked —
                                  by that column's header instead. Aiming at
                                  the line did nothing, which is the one place
                                  everybody aims. */}
                              <span
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={`Resize ${column.name}`}
                                title="Drag to resize · double-click to reset"
                                onPointerDown={event => startResize(column.name, columnWidths[position], event)}
                                onDoubleClick={() => resetWidth(column.name)}
                                onClick={event => event.stopPropagation()}
                                className="absolute top-0 right-0 z-[2] w-3 h-full cursor-col-resize select-none touch-none before:absolute before:inset-y-1 before:right-0 before:w-[3px] before:rounded-full before:bg-transparent hover:before:bg-action/70"
                              />
                            </th>
                          );
                        })}
                        <th aria-hidden className="sticky top-0 z-[1] bg-navy-700 border-b border-navy-500" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.rows.map((row, rowIndex) => {
                        const isOpen = isOpenRow(row);

                        return (
                          <tr
                            key={rowIndex}
                            onClick={() => openDrawer(row)}
                            aria-selected={isOpen}
                            className={[
                              'cursor-pointer transition-colors duration-[120ms]',
                              isOpen
                                ? 'bg-navy-600'
                                : rowIndex % 2 ? 'bg-navy-700/50 hover:bg-navy-600/60' : 'hover:bg-navy-600/60',
                            ].join(' ')}
                          >
                            {canTick ? (
                              <td
                                // The tick is not the row: clicking it must not
                                // open the drawer over the grid being ticked.
                                onClick={event => event.stopPropagation()}
                                className="px-3 py-[11px] border-b border-r border-navy-700"
                              >
                                <input
                                  type="checkbox"
                                  checked={!!ticked[rowKey(row)]}
                                  onChange={() => toggleRow(row)}
                                  aria-label={`Select row ${rowKey(row).split('\u0000').join(', ')}`}
                                  className="b4-focus w-4 h-4 align-middle cursor-pointer accent-action"
                                />
                              </td>
                            ) : null}
                            {visibleColumns.map(({ column, index }, position) => (
                              <td
                                key={column.name}
                                title={titleFor(row[index])}
                                className={[
                                  // One line per row, always: a cell that
                                  // wraps moves every row below it. The whole
                                  // value is on hover and in the drawer.
                                  'px-2.5 py-2 md:px-3.5 md:py-[11px] border-b border-r border-navy-700 last:border-r-0 whitespace-nowrap truncate font-courrier text-ds-115 md:text-ds-125',
                                  position === 0 ? 'text-navy-300' : 'text-navy-100',
                                  // Numbers end at the same edge, which is the
                                  // only way a column of them can be compared.
                                  isNumericType(column.dataType) ? 'text-right' : '',
                                ].join(' ')}
                              >
                                <CellValue value={row[index]} dataType={shortTypeName(column.dataType)} />
                              </td>
                            ))}
                            <td aria-hidden className="border-b border-navy-700" />
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {rows.rows.length === 0 ? (
                    <p className="m-0 px-4 py-8 text-ds-135 text-navy-300">
                      {filters.length ? 'No rows match this filter.' : page === 0 ? 'This table is empty.' : 'No more rows.'}
                    </p>
                  ) : null}
                </div>

                {/* Offset paging, and it says so: "Rows 51–100" is true
                    whatever the estimate beside it is. */}
                {/* What ticking rows is FOR. It sits above the pager rather
                    than floating over the grid: the rows it acts on are the
                    ones on screen, and a bar that covers them to say so is a
                    bar in the way. */}
                {tickedKeys.length > 0 ? (
                  <div className="flex-none flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 border-t border-navy-500 bg-action/10">
                    <span className="font-courrier text-ds-115 text-white">
                      {tickedKeys.length} row{tickedKeys.length === 1 ? '' : 's'} selected
                    </span>
                    {/* Left, like the pager below it: two stacked bars whose
                        buttons sit at opposite ends read as two designs. */}
                    <div className="flex items-center gap-2">
                      <DsButton variant="ghost" size="md" onClick={() => setTicked({})}>
                        Clear
                      </DsButton>
                      <DsButton variant="danger" size="md" onClick={deleteTicked}>
                        <TrashIcon size={14} /> Delete {tickedKeys.length} row{tickedKeys.length === 1 ? '' : 's'}
                      </DsButton>
                    </div>
                  </div>
                ) : null}

                {/* Everything on the left, and the right corner left empty on
                    purpose: the support launcher is fixed over it, and a
                    button nobody can click because a chat bubble sits on it is
                    worse than an empty corner. Reserving padding for it only
                    moved the problem — the buttons still ended up under it on
                    a narrower window. */}
                <div className="flex-none flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-t border-navy-500">
                  <div className="flex items-center gap-2 order-1">
                    <DsButton
                      variant="ghost"
                      size="md"
                      onClick={() => setPage(current => Math.max(0, current - 1))}
                      isDisabled={page === 0 || isLoadingRows}
                    >
                      Previous
                    </DsButton>
                    <DsButton
                      variant="outline"
                      size="md"
                      onClick={() => setPage(current => current + 1)}
                      // A full page is the only evidence there may be another
                      // one — the count is an estimate and cannot decide this.
                      isDisabled={rows.rows.length < PAGE_SIZE || isLoadingRows}
                    >
                      Next
                    </DsButton>
                  </div>
                  <span className="order-2 font-courrier text-ds-115 text-navy-300">{rowsLabel}</span>
                </div>
              </SurfaceCard>

            </> : (
              <SurfaceCard tone="surface" className="p-6">
                <p className="text-ds-135 text-navy-300">We couldn't read this table's columns.</p>
              </SurfaceCard>
            )
          ) : activeTab === 'columns' && selectedTable ? (
            <ColumnsList
              table={selectedTable}
              columns={rows?.columns || []}
              hidden={hidden}
              isTable={isTable}
              isLoading={isLoadingRows}
              onAsk={setDialog}
            />
          ) : activeTab === 'indexes' && selectedTable ? (
            <IndexesList
              table={selectedTable}
              indexes={objects?.indexes || []}
              isLoading={isLoadingObjects}
              error={objectsError}
              onAsk={setDialog}
            />
          ) : activeTab === 'triggers' && selectedTable ? (
            <TriggersList
              table={selectedTable}
              triggers={objects?.triggers || []}
              isLoading={isLoadingObjects}
              error={objectsError}
              onAsk={setDialog}
            />
          ) : activeTab === 'definition' ? (
            definitionSql ? (
              <DefinitionView sql={definitionSql} />
            ) : (
              <p className="m-0 text-ds-135 text-navy-300">We couldn't read this definition.</p>
            )
          ) : null}
        </div>
      </div>
    </div>

    {/* ── The explorer as a sheet, below `xl`. ── */}
    <SideDrawer open={isSheetOpen} onClose={closeSheet} side="bottom" zIndex="z-[70]">
      {/* A sheet, so it says so: the grab bar is what a sheet is recognised
          by, and the head sits on a rule instead of floating over the search
          under it. */}
      <div className="flex-none flex justify-center pt-2.5 pb-1">
        <span className="w-9 h-1 rounded-full bg-navy-500" />
      </div>
      <div className="flex-none flex items-center justify-between gap-3 px-4 py-2 border-b border-navy-700">
        <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">Open</span>
        <DsButton variant="ghost" size="touch" onClick={closeSheet}>Close</DsButton>
      </div>
      <div className="flex-1 min-h-0">{explorer(true)}</div>
    </SideDrawer>

    {/* ── A row, in a drawer from the right. ── */}
    <SideDrawer
      open={activeTab === 'data' && !!selectedTable && !!rows && !!openRow}
      onClose={closeDrawer}
      width="md:w-[460px]"
    >
      {selectedTable && rows && openRow ? (
          <RowInspector
            appId={appId}
            schema={selectedTable.schema}
            table={selectedTable.name}
            isEditable={selectedTable.kind === 'table'}
            columns={rows.columns}
            row={openRow === 'new' ? undefined : openRow}
            onClose={closeDrawer}
            onSaved={next => {
              // An edit stays open on the row as it now reads; an insert is done.
              if (next) {
                setOpenRow(next);
              } else {
                closeDrawer();
              }
              reloadAll();
            }}
            onDeleted={() => {
              closeDrawer();
              reloadAll();
            }}
          />
      ) : null}
    </SideDrawer>

    <SqlDialog
      appId={appId}
      request={dialog}
      onClose={() => setDialog(null)}
      // A statement can have created a table, so the object list is as stale
      // as the open tab — everything is reloaded.
      onDone={reloadAll}
    />

    <AddColumnDialog
      appId={appId}
      table={addColumnTo}
      onClose={() => setAddColumnTo(null)}
      onDone={reloadAll}
    />

    <CreateTableDrawer
      appId={appId}
      open={!!createTable}
      initialName={createTable?.name}
      onClose={() => setCreateTable(null)}
      onCreated={id => {
        setCreateTable(null);
        reloadAll();
        // Opened straight away: the overview reload keeps a selection it
        // finds in the new list, and this one will be in it.
        openObject(id);
      }}
    />
  </>;
};

/**
 * The state for a database that is running and has nothing in it.
 *
 * Not an error and not an offer — the app is already paying and already
 * connected, so it says what a table IS and names the two ways one appears.
 * The button is not here: it is in the toolbar above, where every other green
 * button on this screen lives.
 */
const EmptyDatabase = () => (
  <div className="flex flex-col items-center gap-4 px-6 py-20 text-center">
    <span className="inline-flex items-center justify-center w-16 h-16 rounded-[14px] border border-navy-500 bg-navy-700 text-action shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <DatabaseIcon size={28} />
    </span>
    <p className="m-0 font-sora text-ds-20 font-semibold leading-[1.3] text-white">
      Your database is empty
    </p>
    <p className="m-0 max-w-[52ch] text-ds-135 leading-[1.5] text-navy-200">
      Tables are where your rows live. Create the first one here, or let a
      migration create it on the next deploy.
    </p>
  </div>
);

/**
 * The tab on the rail's edge that closes and reopens it.
 *
 * On the border itself rather than inside either side: it belongs to neither,
 * and put in the rail's own header it disappeared the moment the rail did.
 * `xl` only — below that the list is a sheet, which has its own way in and out.
 */
const RailHandle = ({ isOpen, onClick }: { isOpen: boolean, onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isOpen ? 'Collapse the list' : 'Show the list'}
    aria-expanded={isOpen}
    title={isOpen ? 'Collapse the list' : 'Show the list'}
    className={[
      // Halfway down the border, straddling it. At the top it read as a stray
      // chip floating beside the search field; on the border at mid-height it
      // reads as the seam's own handle, which is what it is.
      'hidden xl:flex absolute top-1/2 -translate-y-1/2 z-[3] items-center justify-center',
      'w-[22px] h-[34px] border border-navy-500 bg-navy-800',
      'text-navy-200 hover:text-white hover:border-navy-300 transition-colors duration-[120ms] b4-focus',
      // Small to look at, comfortable to hit: the tab stays 22px so it does
      // not cover the list, and the click area is grown around it instead.
      'before:absolute before:-inset-2.5 before:content-[""]',
      isOpen ? '-right-[11px] rounded-[6px]' : 'left-0 rounded-r-[6px]',
    ].join(' ')}
  >
    <ChevronRightIcon size={14} className={isOpen ? 'rotate-180' : undefined} />
  </button>
);

/**
 * A square button in the toolbar, holding one icon.
 *
 * Refresh and the SQL console sit here: both act on the table on screen rather
 * than on the tab that happens to be open, and both are recognised by their
 * glyph — spelled out they were the widest thing in a row whose job is the
 * filter. The title carries the name for anyone who needs it.
 *
 * Refresh exists at all because the screen loads its queries once and then
 * only re-runs them after a statement IT sent: everything else that writes to
 * this database leaves the page showing what was true when it opened.
 */
/** The open section, and the others under it — the phone's tab bar. */
const TabsMenu = ({ tabs, active, onPick, className = '' }: {
  tabs: DatabaseTab[],
  active: DatabaseTab,
  onPick: (tab: DatabaseTab) => void,
  className?: string,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);

  useDismiss(rootRef, isOpen, close);

  return (
    <div className={`relative flex-none ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        aria-label="Section"
        className={[
          // The same height and the same border as the icons beside it: this
          // is one row of controls, not a pill next to four buttons. `h-11`
          // and not `min-h-11` — there is no numeric min-height scale in this
          // Tailwind build, so that class was doing nothing at all.
          'b4-focus flex items-center gap-1 h-10 md:h-[30px] px-2 md:px-2.5 rounded-[6px] border border-navy-400 bg-navy-700',
          'font-courrier text-ds-115 uppercase tracking-[0.06em] font-semibold text-white',
        ].join(' ')}
      >
        {TAB_LABEL[active]}
        <ChevronDownIcon size={14} className="text-navy-200" />
      </button>

      {isOpen ? (
        <Popover width={200}>
          {tabs.map(value => (
            <PopoverItem
              key={value}
              role="menuitemradio"
              aria-checked={value === active}
              icon={<CheckIcon size={16} className={value === active ? 'text-state-ok-soft' : 'opacity-0'} />}
              onClick={() => { setIsOpen(false); onPick(value); }}
            >
              {TAB_LABEL[value]}
            </PopoverItem>
          ))}
        </Popover>
      ) : null}
    </div>
  );
};

/**
 * Everything the toolbar holds, on a phone, behind one button.
 *
 * Refresh, export, the console and which columns show are four things done one
 * at a time and rarely — on a narrow screen they were four permanent 44px
 * squares beside a field that needed the width. A menu is the shape for
 * actions that share a place and never share a moment.
 */
const MoreMenu = ({ onExport, canExport, onConsole, columns, hidden, onHiddenChange }: {
  onExport: () => void,
  canExport: boolean,
  onConsole: () => void,
  columns: AppDatabaseColumn[],
  hidden: string[],
  onHiddenChange: (hidden: string[]) => void,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);

  useDismiss(rootRef, isOpen, close);

  const visibleCount = columns.filter(column => !hidden.includes(column.name)).length;
  const act = (run: () => void) => () => { setIsOpen(false); run(); };

  return (
    <div className="relative flex-none" ref={rootRef}>
      <IconButton onClick={() => setIsOpen(open => !open)} label="More" hasTooltip={false}>
        <MoreIcon size={16} />
      </IconButton>

      {isOpen ? (
        <Popover align="right" width={260}>
          <PopoverItem icon={<TerminalIcon size={16} />} onClick={act(onConsole)}>
            SQL console
          </PopoverItem>
          <PopoverItem
            icon={<DownloadIcon size={16} />}
            isDisabled={!canExport}
            onClick={act(onExport)}
          >
            Export this page as CSV
          </PopoverItem>

          {columns.length > 0 ? <>
            <SectionLabel>Columns</SectionLabel>
            <div className="max-h-[240px] overflow-auto b4-thin">
              {columns.map(column => {
                const isOn = !hidden.includes(column.name);

                return (
                  <PopoverItem
                    key={column.name}
                    role="menuitemcheckbox"
                    aria-checked={isOn}
                    // A grid of no columns is not a state anyone means to reach.
                    isDisabled={isOn && visibleCount === 1}
                    icon={<CheckIcon size={16} className={isOn ? 'text-state-ok-soft' : 'opacity-0'} />}
                    onClick={() => onHiddenChange(isOn
                      ? [...hidden, column.name]
                      : hidden.filter(name => name !== column.name))}
                  >
                    <span className="font-courrier">{column.name}</span>
                  </PopoverItem>
                );
              })}
            </div>
          </> : null}
        </Popover>
      ) : null}
    </div>
  );
};

const IconButton = ({ onClick, label, isBusy = false, isDisabled = false, hasTooltip = true, children }: {
  onClick: () => void,
  label: string,
  isBusy?: boolean,
  /** Nothing to act on — an empty page has nothing to export. */
  isDisabled?: boolean,
  /**
   * The browser's own tooltip. Off where there is no pointer to hover with: on
   * a phone it fires on a long press and lands wherever the browser decides —
   * which was on top of the table's name, in a grey box of its own design.
   */
  hasTooltip?: boolean,
  children: React.ReactNode,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isBusy || isDisabled}
    aria-label={label}
    title={hasTooltip ? label : undefined}
    className="b4-focus flex-none flex items-center justify-center w-10 h-10 md:w-[30px] md:h-[30px] rounded-[6px] border border-navy-400 text-navy-200 hover:text-white hover:border-navy-300 disabled:opacity-60 transition-colors duration-[120ms]"
  >
    {children}
  </button>
);

export default MyAppDatabase;
